
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MediaPickerModal } from "@/components/email-builder/MediaPickerModal";
import { Select } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { DocumentConfiguration } from "@/types/email";
import { PaddingControls } from "../shared";
import { ColorPicker } from "../shared/ColorPicker";

interface DocumentPropertiesProps {
  config: DocumentConfiguration;
  onChange: (config: DocumentConfiguration) => void;
}

/**
 * DocumentProperties Component
 * 
 * Property panel for document-level configuration (email shell).
 * Controls layout, background, and spacing for the entire document.
 * 
 * @param config - Current document configuration
 * @param onChange - Callback when configuration changes
 */
export function DocumentProperties({ config, onChange }: DocumentPropertiesProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const updateField = <K extends keyof DocumentConfiguration>(
    field: K,
    value: DocumentConfiguration[K]
  ) => {
    onChange({ ...config, [field]: value });
  };

  return (
    <div className="space-y-3">
      {/* Layout Section */}
      <Accordion type="multiple" defaultValue={["layout", "background"]} className="w-full">
        <AccordionItem value="layout">
          <AccordionTrigger className="text-sm font-medium">
            Layout
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-4">
            {/* Width */}
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Email Width (px)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={200}
                  max={1200}
                  value={config.width}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value >= 200 && value <= 1200) {
                      updateField('width', value);
                    }
                  }}
                  className="flex-1"
                />
                <span className="text-xs text-muted-foreground">200-1200</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Standard email width is 600px
              </p>
            </div>

            {/* Alignment */}
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Email Alignment
              </Label>
              <Select
                value={config.alignment}
                onChange={(e) => updateField('alignment', e.target.value as DocumentConfiguration['alignment'])}
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </Select>
            </div>

          </AccordionContent>
        </AccordionItem>

        {/* Background Section */}
        <AccordionItem value="background">
          <AccordionTrigger className="text-sm font-medium">
            Background
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-4">
            {/* Canvas Background Color */}
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Canvas Background Color
              </Label>
              <ColorPicker
                value={config.canvasBackgroundColor || '#f4f4f4'}
                onChange={(color) => updateField('canvasBackgroundColor', color)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                The page background surrounding the email — visible in email clients that show a canvas behind the email body
              </p>
            </div>

            {/* Content Background Color */}
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Content Background Color
              </Label>
              <ColorPicker
                value={config.backgroundColor || '#ffffff'}
                onChange={(color) => updateField('backgroundColor', color)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                The background of the email body itself
              </p>
            </div>

            {/* Background Image URL */}
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Background Image URL
                <span className="text-muted-foreground ml-1">(optional)</span>
              </Label>
              <div className="flex items-center gap-1.5">
                <Input
                  type="url"
                  value={config.backgroundImage || ''}
                  onChange={(e) => updateField('backgroundImage', e.target.value || undefined)}
                  placeholder="https://example.com/image.jpg"
                  className="flex-1 min-w-0"
                />
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="shrink-0 rounded-md border border-input bg-background p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  title="Pick from media library"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Image displayed behind email content
              </p>
              <MediaPickerModal
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={(url) => updateField('backgroundImage', url)}
              />
            </div>

            {/* Background Repeat (only show if image URL is set) */}
            {config.backgroundImage && (
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  Background Repeat
                </Label>
                <Select
                  value={config.backgroundRepeat}
                  onChange={(e) =>
                    updateField('backgroundRepeat', e.target.value as DocumentConfiguration['backgroundRepeat'])
                  }
                >
                  <option value="no-repeat">No Repeat</option>
                  <option value="repeat">Repeat</option>
                  <option value="repeat-x">Repeat Horizontally</option>
                  <option value="repeat-y">Repeat Vertically</option>
                </Select>
              </div>
            )}

            {/* Background Position (only show if image URL is set) */}
            {config.backgroundImage && (
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  Background Position
                </Label>
                <Select
                  value={config.backgroundPosition}
                  onChange={(e) =>
                    updateField('backgroundPosition', e.target.value as DocumentConfiguration['backgroundPosition'])
                  }
                >
                  <option value="top">Top</option>
                  <option value="center">Center</option>
                  <option value="bottom">Bottom</option>
                </Select>
              </div>
            )}

            {/* Preview (if image URL is set) */}
            {config.backgroundImage && (
              <div>
                <Label className="text-xs font-medium mb-1 block">Preview</Label>
                <div
                  className="w-full h-16 rounded-md border border-border"
                  style={{
                    backgroundColor: config.backgroundColor,
                    backgroundImage: `url(${config.backgroundImage})`,
                    backgroundRepeat: config.backgroundRepeat,
                    backgroundPosition: config.backgroundPosition,
                  }}
                />
              </div>
            )}
          </AccordionContent>
        </AccordionItem>

        {/* Spacing Section */}
        <AccordionItem value="spacing">
          <AccordionTrigger className="text-sm font-medium">
            Spacing
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-4">
            <div>
              <PaddingControls
                value={config.padding}
                onChange={(padding) => updateField('padding', padding)}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Padding around the entire email content
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Advanced Section */}
        <AccordionItem value="advanced">
          <AccordionTrigger className="text-sm font-medium text-muted-foreground">
            Advanced
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pt-4">
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Language
                <span className="text-muted-foreground ml-1">(optional)</span>
              </Label>
              <Input
                type="text"
                maxLength={10}
                value={config.language || ''}
                onChange={(e) => updateField('language', e.target.value || undefined)}
                placeholder="en"
              />
              <p className="text-xs text-muted-foreground mt-1">
                ISO language code — affects screen reader pronunciation (e.g. en, fr, de)
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
